import moment from 'moment';
import { isEqual } from 'lodash-es';
import {
  AdminServiceLog,
  BookingStateChangeTypes, IAccount,
  IBooking,
  IBookingFromPouch,
  IPaymentDetailsBase,
  ITableItem, IVenue
} from 'shared-types/index';

import { IconTypes } from 'external-services/icons-service/icons.types';
import PouchLoadService from 'external-services/pouch-load-service/index';
import { IBookingChangeState, IChangeListItem, IChanges } from './type';
import DateUtilsService from "external-services/date-utils-service";
import LocationService from "external-services/location-service";
import NetworkConnectionService from "external-services/network-connection-service";
import AdminService from "external-services/diary-admin-service/admin.service";
import NetworkRequestsService from 'external-services/network-requests-service';

const NS = 'BookingChangeService'; // jshint ignore:line

class BookingChangeService {

  private account: IAccount;
  private getVenue: () => IVenue;

  public setup(account: IAccount, getVenue: () => IVenue): void {
    this.account = account;
    this.getVenue = getVenue;
  }

  /**
   * @param {string} type - The change type, see available values listed above
   * @param {*} booking - The booking obj after changes
   * @param {*} [originalBooking] - The booking obj before changes
   * @param {*} [info] - Additional info, vary based on type input
   */
  public saveBookingChange(
    type: BookingStateChangeTypes,
    booking: IBooking | IBookingFromPouch,
    originalBooking: IBooking | IBookingFromPouch,
    staff: string = null,
    info: number = null,
    callee: string = ""
  ): Promise<void> {
    return this.getChanges(booking, originalBooking)
      .then((changesObj: IBookingChangeState) => {
        const changesList: IChangeListItem[] = [];

        if (type === BookingStateChangeTypes.UPDATED || type === BookingStateChangeTypes.MOVED) {
          // Check for People and Duration changes
          ['people', 'duration'].forEach((property: string) => {
            // check for a change
            if ((booking as any)[property] !== (originalBooking as any)[property]) {
              changesList.push({
                property: property,
                originalValue: (originalBooking as any)[property],
                newValue: (booking as any)[property],
              });
            }
          });

          // Time
          const time: string = moment(booking.time).format('YYYY/MM/DD HH:mm');
          const originalTime: string = moment(originalBooking.time).format('YYYY/MM/DD HH:mm');
          if (time !== originalTime) {
            changesList.push({
              property: 'time',
              originalValue: originalTime,
              newValue: time,
            });
          }

          // Check for tables changes
          if (originalBooking && originalBooking.tables) {
            const oldTableIds: string[] = originalBooking.tables.map(
              (table: ITableItem) => table._id
            );

            let newTableIds: string[];
            if (booking.tables) {
              newTableIds = booking.tables.map((table: ITableItem) => table._id);
            }

            if (!isEqual(oldTableIds, newTableIds)) {
              const oldTables: string[] = originalBooking.tables.map(
                (table: ITableItem) => 'T' + table.name
              );
              const newTables: string[] = booking.tables.map(
                (table: ITableItem) => 'T' + table.name
              );
              const originalValue: string = oldTables.join(',');
              const newValue: string = newTables.length > 0 ? newTables.join(',') : 'Wait List';
              changesList.push({
                property: 'tables',
                originalValue: originalValue,
                newValue: newValue,
              });
            }
          }

          // check for Status changes, but only if booking wasn't moved
          if (
            type !== BookingStateChangeTypes.MOVED &&
            originalBooking.status.name !== booking.status.name
          ) {
            changesList.push({
              property: 'status',
              originalValue: originalBooking.status.name,
              newValue: booking.status.name,
            });
          }

          // check for Notes changes
          if (originalBooking.notes !== booking.notes) {
            changesList.push({
              property: 'notes',
              originalValue: originalBooking.notes,
              newValue: booking.notes ? booking.notes : 'Note Removed',
            });
          }

          // check for bookedBy changes
          if (originalBooking.bookedBy?.firstName !== booking.bookedBy?.firstName) {
            changesList.push({
              property: 'bookedBy-firstName',
              originalValue: originalBooking.bookedBy?.firstName,
              newValue: booking.bookedBy?.firstName || ' '
            })
          }
          if(originalBooking.bookedBy?.lastName !== booking.bookedBy?.lastName) {
            changesList.push({
              property: 'bookedBy-surName',
              originalValue: originalBooking.bookedBy?.lastName,
              newValue: booking.bookedBy?.lastName || ' '
            })
          }
          if(originalBooking.bookedBy?.phone !== booking.bookedBy?.phone) {
            changesList.push({
              property: 'bookedBy-phone',
              originalValue: originalBooking.bookedBy?.phone,
              newValue: booking.bookedBy?.phone || ' '
            })
          }
          if(originalBooking.bookedBy?.email !== booking.bookedBy?.email) {
            changesList.push({
              property: 'bookedBy-email',
              originalValue: originalBooking.bookedBy?.email,
              newValue: booking.bookedBy?.email || ' '
            })
          }
          if(originalBooking.bookedBy?.company !== booking.bookedBy?.company) {
            changesList.push({
              property: 'bookedBy-company',
              originalValue: originalBooking.bookedBy?.company,
              newValue: booking.bookedBy?.company || ' '
            })
          }

          // check for payment changes
          const originalPaymentDetails: Partial<IPaymentDetailsBase> =
            originalBooking.paymentDetails ? originalBooking.paymentDetails : {};
          const newPaymentDetails: Partial<IPaymentDetailsBase> = booking.paymentDetails
            ? booking.paymentDetails
            : {};

          if (originalPaymentDetails.paymentType !== newPaymentDetails.paymentType && !!originalPaymentDetails.paymentType) {
            changesList.push({
              property: 'Payment Type',
              originalValue: originalPaymentDetails.paymentType,
              newValue: newPaymentDetails.paymentType,
            });
          }

          if (originalPaymentDetails.price !== newPaymentDetails.price) {
            changesList.push({
              property: 'Payment Price',
              originalValue: originalPaymentDetails.price,
              newValue: newPaymentDetails.price,
            });
          }

          if (originalPaymentDetails.promotionCode !== newPaymentDetails.promotionCode) {
            changesList.push({
              property: 'Promotion Code',
              originalValue: originalPaymentDetails.promotionCode,
              newValue: newPaymentDetails.promotionCode,
            });
          }

          if (originalPaymentDetails.discountAmount !== newPaymentDetails.discountAmount) {
            changesList.push({
              property: 'Discount Amount',
              originalValue: originalPaymentDetails.discountAmount,
              newValue: newPaymentDetails.discountAmount,
            });
          }

          if (originalPaymentDetails.peopleRequired !== newPaymentDetails.peopleRequired) {
            changesList.push({
              property: 'People Required',
              originalValue: originalPaymentDetails.peopleRequired,
              newValue: newPaymentDetails.peopleRequired,
            });
          }
        }

        if (type === BookingStateChangeTypes.PRE_AUTH_RELEASED) {
          // No changesList entry to register, just type and date will do
        }

        if (type === BookingStateChangeTypes.PRE_AUTH_CHARGED) {
          changesList.push({
            property: 'amount',
            originalValue: null,
            newValue: info,
          });
        }

        // Guard from no changes
        if (type === BookingStateChangeTypes.UPDATED && !changesList.length) {
          return;
        }

        const bookingChange: IChanges = {
          time: new Date().toISOString(),
          method: 'phone',
          type: type,
          source: NetworkRequestsService.getNBISource(),
          changeList: changesList,
        };

        if (staff) {
          bookingChange.staffName = staff;
        }

        try {

          const dateIsMismatched = this.isDateMismatchedCheck(booking);
          const dateMismatchChanges: any[] = [];

          bookingChange.changeList.forEach(change => {
            if (dateIsMismatched) {
              this.gatherDateMismatchData(dateMismatchChanges, change, booking, originalBooking, staff, callee);
            }
          });

          if (dateMismatchChanges.length) {
            const notes = JSON.stringify(dateMismatchChanges);
            console.log(NS, 'dateMismatchChanges', notes);
            AdminService.serverLoggingDbEvents(this.getVenue().id.toString(), "Booking changes ID Date Mismatch", AdminServiceLog.WARNLOG, notes);
          }

        } catch (err) {
          console.warn(NS, 'failed to gather Date Mismatch Data', err);
        }

        // Add changes entry
        changesObj.changes.push(bookingChange);

        // Update database record
        return PouchLoadService.selectDatabase(booking.time).put(changesObj) as any;
      })
      .catch((err) => {
        console.error('Failed to save booking changes', err);
      });
  }

  //takes date from id (eg "booking_2023-08-15_B6QPYF6BZ7QIT_1691987863473", gets "2023-08-15")
  private isDateMismatchedCheck(booking: IBooking | IBookingFromPouch): boolean {
    const bookingIdDatePrefix = booking._id.split("_")[1];
    const timeAsDate: Date = typeof booking.time === "string" ? DateUtilsService.dateStrToDateTime(booking.time) : booking.time;
    const bookingTimeStr = DateUtilsService.dateToIdStr(timeAsDate);

    return bookingIdDatePrefix !== bookingTimeStr;
  }

  private gatherDateMismatchData(
    dateMismatchChanges: any[],
    change: IChangeListItem,
    booking: IBooking | IBookingFromPouch,
    originalBooking: IBooking | IBookingFromPouch,
    staff: string,
    callee: string
  ): void {
    const timeAsDate: Date = typeof booking.time === "string" ? DateUtilsService.dateStrToDateTime(booking.time) : booking.time;
    const origTimeAsDate: Date = typeof originalBooking.time === "string" ? DateUtilsService.dateStrToDateTime(originalBooking.time) : originalBooking.time;

    dateMismatchChanges.push({
      ...change,
      name: (this.account.firstName || '(no first name)') + (this.account.surname || '(no last name)'),
      email: (this.account.email || '(no email)'),
      bookingId: `ORIGINAL: ${originalBooking._id} / NEW: ${booking._id}`,
      bookingTime: `ORIGINAL: ${origTimeAsDate.toString()} / NEW: ${timeAsDate.toString()}`,
      bookingType: `ORIGINAL: ${originalBooking.bookingType} / NEW: ${booking.bookingType}`,
      bookingMethod: `ORIGINAL: ${originalBooking.method} / NEW: ${booking.method}`,
      deviceDetails: LocationService.deviceDetails(),
      online: NetworkConnectionService.isOnline(),
      staff: staff || "(not set)",
      callee: callee || "(not set)",
      href: window.location.href // will help use determine Diary Mobile VS Diary
    })
  }

  public getChanges(
    booking: IBooking | IBookingFromPouch,
    originalBooking: IBooking | IBookingFromPouch = null
  ): Promise<IBookingChangeState> {
    return new Promise((resolve) => {
      const emptyChanges: IBookingChangeState = this.emptyDOC(booking._id);
      let database: any = PouchLoadService.selectDatabase(booking.time);

      database
        .get('changes_' + booking._id)
        .then((entry: IBookingChangeState) => {
          // console.log('GET Changes of booking');
          resolve(entry);
        })
        .catch(
          (err: {
            error: boolean;
            message: string;
            name: string;
            reason: string;
            status: number;
          }) => {
            if (err.status === 404 && originalBooking && originalBooking._id) {
              database = PouchLoadService.selectDatabase(originalBooking.time);
              // changes for this booking were not found copy accross the ones for the moveBooking
              database
                .get('changes_' + originalBooking._id)
                .then((changes: IBookingChangeState) => {
                  changes._id = 'changes_' + booking._id;
                  delete changes._rev;
                  // console.log('GET Changes of originalBooking');
                  resolve(changes);
                })
                .catch(() => {
                  // console.log('GET empty changes');
                  resolve(emptyChanges);
                });
            } else if (err.status === 404) {
              // console.log('GET empty changes');
              resolve(emptyChanges);
            } else {
              // console.log('GET empty changes');
              resolve(emptyChanges);
            }
          }
        );
    });
  }

  public getChangeInfo(
    changeList: IChangeListItem[],
    type: BookingStateChangeTypes
  ): { comment: string; formattedChangeList: IChangeListItem[] } {
    const dolarString = '$$';
    const dateTimeFormat = type === BookingStateChangeTypes.MOVED ? 'DD MMM YY h:m A' : 'h:m a';
    const toDateTimeFormat = (str: string | number) =>
      moment(str).format(dateTimeFormat).toUpperCase();

    let comment = '';
    const formattedChangeList = changeList.map((listItem) => {
      const { property, originalValue, newValue } = listItem;
      let newValueSliced: string[] = [];
      if (property.toLowerCase() === 'time') {
        if (typeof newValue === 'string' && newValue.includes(dolarString)) {
          newValueSliced = String(newValue).split(dolarString);
          comment = newValueSliced[1]; // comment is overwritten after each loop
        }
        return {
          property,
          originalValue: toDateTimeFormat(originalValue),
          newValue: newValueSliced.length ? newValueSliced[0] : toDateTimeFormat(newValue),
        };
      }

      return listItem;
    });
    return {
      formattedChangeList,
      comment,
    };
  }

  public getChangeIcon(
    method: string,
    changeList: IChangeListItem[],
    returnAsIcon: boolean
  ): string | IconTypes {
    const iconMap: Record<string, IconTypes> = {
      'mdi-email-outline': IconTypes.mail,
      'mdi-server-off': IconTypes.serverOff,
      'mdi-cellphone': IconTypes.phone,
      'mdi-rss': IconTypes.rss,
      'mdi-book-open': IconTypes.bookings,
      'mdi-phone-in-talk': IconTypes.phoneInTalk,
      'reserve-with-google': IconTypes.googleIcon,
    };

    const correctIconType = (iconString: string) =>
      returnAsIcon ? iconMap[iconString] : iconString;
    if (method === 'system') {
      const media = changeList.length > 0 ? changeList[0].property.toLowerCase() : 'unknown';
      const iconString =
        media === 'unknown'
          ? 'mdi-server-off'
          : media.includes('email')
          ? 'mdi-email-outline'
          : 'mdi-cellphone';
      return correctIconType(iconString);
    }

    const iconString =
      method === 'phone' ? 'mdi-book-open' 
        : method === 'sadie-ai' ? 'mdi-phone-in-talk'
        : method.startsWith('API - ') ? 'mdi-rss' 
        : method;
    return correctIconType(iconString);
  }

  private emptyDOC(bookingId: string): IBookingChangeState {
    return {
      _id: 'changes_' + bookingId,
      type: 'changes',
      changes: [],
    };
  }
}

const instance = new BookingChangeService();
export default instance as BookingChangeService;
